import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { TableUrlStateAdapter } from '../../Table';
import {
  encodeSortParam,
  parseListParam,
  parseSortParam
} from '../../../lib/listState/urlEncoding';
import { getWorkItemsView, setWorkItemsView } from '../../../lib/viewPreferences';
import {
  EMPTY_FILTERS,
  parseGroupBy,
  type GroupBy,
  type WorkItemFilters,
  type WorkItemsTab
} from '../types';
import {
  applyColAliases,
  COLS_STORAGE_KEY,
  DEFAULT_VISIBLE_COLS,
  parseTab
} from './tableColumns';

/**
 * Single source of truth for "how the Work Items tab is read from / written to"
 * the durable global preference (`penrose:work-items:view`). Used by BOTH the
 * URL-backed hook (on top of its `?tab=` precedence) and the component-local
 * hook, so the persistence rule cannot drift between the two surfaces.
 */
export function resolveInitialWorkItemsTab(): WorkItemsTab {
  return getWorkItemsView() ?? 'board';
}

export function persistWorkItemsTab(next: WorkItemsTab): void {
  // setWorkItemsView already ignores the non-persistable 'timeline'.
  setWorkItemsView(next);
}

/**
 * The full view state consumed by <WorkItemsView>. Each host supplies a
 * different implementation: the standalone /work-items page backs it with URL
 * search params + localStorage (so deep-links + reloads round-trip); the
 * embedded Project-detail instance backs it with component-local state (so it
 * never pollutes the project-canvas URL or collides on localStorage).
 */
export interface WorkItemsViewState {
  tab: WorkItemsTab;
  setTab: (next: WorkItemsTab) => void;

  filters: WorkItemFilters;
  setFilters: (next: WorkItemFilters) => void;

  groupBy: GroupBy;
  setGroupBy: (next: GroupBy) => void;

  searchQuery: string;
  setSearchQuery: (q: string) => void;

  visibleCols: ReadonlyArray<string>;
  setVisibleCols: (next: ReadonlyArray<string>) => void;

  /** Handed straight to <Table urlState=>; encapsulates the sort + cols round-trip. */
  tableUrlAdapter: TableUrlStateAdapter;
}

/**
 * URL-backed view state for the standalone /work-items page. Lifted verbatim
 * from the old WorkItems route body so the page's deep-link / reload /
 * localStorage contract is preserved bit-for-bit.
 */
export function useUrlWorkItemsViewState(): WorkItemsViewState {
  const [params, setParams] = useSearchParams();

  // An explicit `?tab=` wins (share / deep links); otherwise restore the
  // locally-stored preference, falling back to Board.
  const rawTab = params.get('tab');
  const tab = rawTab !== null ? parseTab(rawTab) : resolveInitialWorkItemsTab();

  const setTab = useCallback(
    (next: WorkItemsTab) => {
      const nextParams = new URLSearchParams(params);
      if (next === 'board') {
        nextParams.delete('tab');
      } else {
        nextParams.set('tab', next);
      }
      setParams(nextParams, { replace: false });
      // Remember the choice locally (ignores 'timeline' — see viewPreferences).
      persistWorkItemsTab(next);
    },
    [params, setParams]
  );

  const filters: WorkItemFilters = useMemo(
    () => ({
      type: parseListParam(params.get('type')),
      status: parseListParam(params.get('status')),
      assignee: parseListParam(params.get('assignee')),
      epic: parseListParam(params.get('epic')),
      priority: parseListParam(params.get('priority')),
      sprint: parseListParam(params.get('sprint'))
    }),
    [params]
  );

  const groupBy: GroupBy = useMemo(
    () => parseGroupBy(params.get('groupBy')),
    [params]
  );

  const searchQuery = params.get('q') ?? '';

  // Single facet-update entry point used by the +Filter popover and the
  // active-filter chips' × buttons. Writes every non-empty facet to the URL
  // as comma-joined ids; deletes the param when the facet's values are empty
  // so the URL stays short on the no-filter happy path.
  const setFilters = useCallback(
    (next: WorkItemFilters) => {
      const nextParams = new URLSearchParams(params);
      Object.keys(next).forEach((key) => {
        const values = next[key];
        if (values.length === 0) nextParams.delete(key);
        else nextParams.set(key, values.join(','));
      });
      setParams(nextParams, { replace: true });
    },
    [params, setParams]
  );

  const setSearchQuery = useCallback(
    (q: string) => {
      const nextParams = new URLSearchParams(params);
      if (q === '') nextParams.delete('q');
      else nextParams.set('q', q);
      setParams(nextParams, { replace: true });
    },
    [params, setParams]
  );

  const setGroupBy = useCallback(
    (next: GroupBy) => {
      const nextParams = new URLSearchParams(params);
      if (next === 'none') nextParams.delete('groupBy');
      else nextParams.set('groupBy', next);
      setParams(nextParams, { replace: true });
    },
    [params, setParams]
  );

  const sortFromUrl = useMemo(
    () => parseSortParam(params.get('sort')),
    [params]
  );

  // Resolve the initial column visibility from URL → localStorage → defaults.
  // We only check storage on first mount; after that the URL `?cols=` param
  // is the single source of truth so the visible set round-trips on reload
  // and is shareable in the URL.
  const initialColsFromUrl = useMemo(
    () => applyColAliases(parseListParam(params.get('cols'))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [visibleCols, setVisibleColsState] = useState<ReadonlyArray<string>>(
    () => {
      if (initialColsFromUrl.length > 0) return initialColsFromUrl;
      if (typeof window !== 'undefined') {
        const raw = window.localStorage.getItem(COLS_STORAGE_KEY);
        if (raw) {
          const parsed = applyColAliases(
            raw.split(',').map((s) => s.trim()).filter(Boolean)
          );
          if (parsed.length > 0) return parsed;
        }
      }
      return DEFAULT_VISIBLE_COLS;
    }
  );

  // Keep the URL + localStorage in lock-step with the live visible set.
  const setVisibleCols = useCallback(
    (next: ReadonlyArray<string>) => {
      setVisibleColsState(next);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(COLS_STORAGE_KEY, next.join(','));
      }
      const nextParams = new URLSearchParams(params);
      const matchesDefaults =
        next.length === DEFAULT_VISIBLE_COLS.length &&
        next.every((k, i) => k === DEFAULT_VISIBLE_COLS[i]);
      if (matchesDefaults) {
        nextParams.delete('cols');
      } else {
        nextParams.set('cols', next.join(','));
      }
      setParams(nextParams, { replace: true });
    },
    [params, setParams]
  );

  // Ensure the URL reflects any non-default initial set sourced from storage.
  useEffect(() => {
    if (initialColsFromUrl.length > 0) return;
    const matchesDefaults =
      visibleCols.length === DEFAULT_VISIBLE_COLS.length &&
      visibleCols.every((k, i) => k === DEFAULT_VISIBLE_COLS[i]);
    if (matchesDefaults) return;
    const nextParams = new URLSearchParams(params);
    nextParams.set('cols', visibleCols.join(','));
    setParams(nextParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Both branches of `write` MUST gate on a same-value check. Table's
  // "sync to URL" effect re-fires whenever its `urlState` reference changes;
  // every unconditional setParams here mutates `params`, regenerates this
  // useMemo, hands Table a new `urlState`, and re-triggers the effect — busting
  // the browser's 100-replaceState-per-10s quota. Mirrors the same gating in
  // Documents.tsx's buildTableUrlAdapter.
  const tableUrlAdapter: TableUrlStateAdapter = useMemo(
    () => ({
      read: () => ({
        sort: sortFromUrl ?? undefined,
        cols: [...visibleCols]
      }),
      write: (state) => {
        const desiredSort = encodeSortParam(state.sort) ?? null;
        const currentSort = params.get('sort');
        if (desiredSort !== currentSort) {
          const next = new URLSearchParams(params);
          if (desiredSort) next.set('sort', desiredSort);
          else next.delete('sort');
          setParams(next, { replace: true });
        }
        if (state.cols && state.cols.length > 0) {
          const sameLen = state.cols.length === visibleCols.length;
          const sameOrder = sameLen && state.cols.every((k, i) => k === visibleCols[i]);
          if (!sameOrder) setVisibleCols(state.cols);
        }
      }
    }),
    [params, setParams, sortFromUrl, visibleCols, setVisibleCols]
  );

  return {
    tab,
    setTab,
    filters,
    setFilters,
    groupBy,
    setGroupBy,
    searchQuery,
    setSearchQuery,
    visibleCols,
    setVisibleCols,
    tableUrlAdapter
  };
}

/**
 * Component-local view state for an embedded instance (Project detail). No
 * router, no URL, no localStorage — so it can't pollute the host page's URL or
 * collide with the org page's stored column choice. The table's sort + cols
 * live in plain state, so the replaceState-quota concern doesn't apply.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function useLocalWorkItemsViewState(_opts?: {
  // Retained for source-compatibility only. The tab is now seeded from the
  // global preference (`penrose:work-items:view`), so `defaultTab` is
  // superseded and has no effect.
  defaultTab?: WorkItemsTab;
}): WorkItemsViewState {
  const [tab, setTabState] = useState<WorkItemsTab>(
    // Seed from the global preference (stored → default). No URL read here:
    // the project canvas intentionally keeps view state out of its URL.
    () => resolveInitialWorkItemsTab()
  );
  const setTab = useCallback((next: WorkItemsTab) => {
    setTabState(next);
    persistWorkItemsTab(next);
  }, []);
  const [filters, setFilters] = useState<WorkItemFilters>(EMPTY_FILTERS);
  const [groupBy, setGroupBy] = useState<GroupBy>('none');
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCols, setVisibleCols] =
    useState<ReadonlyArray<string>>(DEFAULT_VISIBLE_COLS);
  const [sort, setSort] = useState<
    ReturnType<typeof parseSortParam> | undefined
  >(undefined);

  const tableUrlAdapter: TableUrlStateAdapter = useMemo(
    () => ({
      read: () => ({
        sort: sort ?? undefined,
        cols: [...visibleCols]
      }),
      write: (state) => {
        setSort(state.sort ?? undefined);
        if (state.cols && state.cols.length > 0) {
          const sameLen = state.cols.length === visibleCols.length;
          const sameOrder =
            sameLen && state.cols.every((k, i) => k === visibleCols[i]);
          if (!sameOrder) setVisibleCols(state.cols);
        }
      }
    }),
    [sort, visibleCols]
  );

  return {
    tab,
    setTab,
    filters,
    setFilters,
    groupBy,
    setGroupBy,
    searchQuery,
    setSearchQuery,
    visibleCols,
    setVisibleCols,
    tableUrlAdapter
  };
}
